import * as React from 'react';

import type {SalesAutomationClosedLeadAutomation as SalesAutomationClosedLeadAutomationFragment} from 'nutshell-graphql-types';

import {groupIntegrationsByActiveStatus} from '../constants';
import type {AvailableIntegrations} from '../types';

import {ClosedLeadAccountTypeAutomations} from './board/closed-lead-account-type-automations';
import {ClosedLeadConstantContactSubscriptions} from './board/closed-lead-constant-contact-subscriptions';
import {ClosedLeadDripSequenceStartTrigger} from './board/closed-lead-drip-sequence-start-triggers';
import {ClosedLeadFollowupRules} from './board/closed-lead-followup-rules/closed-lead-followup-rules';
import {ClosedLeadMailChimpSubscriptions} from './board/closed-lead-mailchimp-subscriptions';
import {ClosedLeadSlackAutomations} from './board/closed-lead-slack-automations';
import {ClosedLeadTasks} from './board/closed-lead-tasks';

type Props = {
    stagesetId: string;
    closedLeadAutomations: SalesAutomationClosedLeadAutomationFragment[];
    isLoading: boolean;
    availableIntegrations: AvailableIntegrations;
    errorMessage: string | null | undefined;
    onRefetchStageset: () => any;
};

export function SalesAutomationClosedLeadsBoard(props: Props) {
    const accountTypeAutomations = (
        <ClosedLeadAccountTypeAutomations
            isLoading={props.isLoading}
            errorMessage={props.errorMessage}
            closedLeadAutomations={props.closedLeadAutomations}
        />
    );

    if (!NutCapabilities.SAGoals.isAvailable) {
        return (
            <div>
                {accountTypeAutomations}
                <ClosedLeadDripSequenceStartTrigger
                    isLoading={props.isLoading}
                    errorMessage={props.errorMessage}
                    closedLeadAutomations={props.closedLeadAutomations}
                />
                {NutClientConfig.canSlackAutomation ? (
                    <ClosedLeadSlackAutomations
                        // @ts-expect-error
                        stagesetId={props.stagesetId}
                        isLoading={props.isLoading}
                        errorMessage={props.errorMessage}
                        hasSlackIntegration={props.availableIntegrations.hasSlackIntegration}
                        closedLeadAutomations={props.closedLeadAutomations}
                    />
                ) : undefined}
            </div>
        );
    } else {
        const emailIntegrationConfig = {
            constantContactEnabled:
                props.availableIntegrations.hasConstantContactIntegration || false,
            mailchimpEnabled: props.availableIntegrations.hasMailChimpIntegration || false,
        } as const;
        const hasEmailMarketingEnabled =
            emailIntegrationConfig.constantContactEnabled ||
            emailIntegrationConfig.mailchimpEnabled;
        // @ts-expect-error
        let integrationsList = [];
        if (hasEmailMarketingEnabled) {
            if (props.availableIntegrations.hasMailChimpIntegration) {
                // @ts-expect-error
                integrationsList = integrationsList.concat([
                    {
                        isActive: true,
                        display: true,
                        component: (
                            <ClosedLeadMailChimpSubscriptions
                                key='MailChimp'
                                // @ts-expect-error
                                stagesetId={props.stagesetId}
                                isLoading={props.isLoading}
                                hasMailChimpIntegration={
                                    props.availableIntegrations.hasMailChimpIntegration
                                }
                                errorMessage={props.errorMessage}
                                closedLeadAutomations={props.closedLeadAutomations}
                            />
                        ),
                    },
                ]);
            }
            if (props.availableIntegrations.hasConstantContactIntegration) {
                integrationsList = integrationsList.concat([
                    {
                        isActive: true,
                        display: true,
                        component: (
                            <ClosedLeadConstantContactSubscriptions
                                key='ConstantContact'
                                // @ts-expect-error
                                stagesetId={props.stagesetId}
                                isLoading={props.isLoading}
                                hasConstantContactIntegration={
                                    props.availableIntegrations.hasConstantContactIntegration
                                }
                                errorMessage={props.errorMessage}
                                closedLeadAutomations={props.closedLeadAutomations}
                                onConnectConstantContact={props.onRefetchStageset}
                            />
                        ),
                    },
                ]);
            }
        } else {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            undefined;
        }
        integrationsList = integrationsList.concat([
            {
                isActive: props.availableIntegrations.hasSlackIntegration || false,
                display: NutClientConfig.canSlackAutomation,
                component: (
                    <ClosedLeadSlackAutomations
                        key='Slack'
                        isLoading={props.isLoading}
                        errorMessage={props.errorMessage}
                        hasSlackIntegration={props.availableIntegrations.hasSlackIntegration}
                        closedLeadAutomations={props.closedLeadAutomations}
                    />
                ),
            },
        ]);

        return (
            <div>
                {NutClientConfig.hasFollowupLeadAutomationsFF ? (
                    <ClosedLeadFollowupRules
                        isLoading={props.isLoading}
                        errorMessage={props.errorMessage}
                        closedLeadAutomations={props.closedLeadAutomations}
                    />
                ) : undefined}
                {NutCapabilities.SAOpenLeadTasks.isAvailable ? (
                    <ClosedLeadTasks
                        isLoading={props.isLoading}
                        errorMessage={props.errorMessage}
                        closedLeadAutomations={props.closedLeadAutomations}
                    />
                ) : undefined}
                {accountTypeAutomations}
                <ClosedLeadDripSequenceStartTrigger
                    isLoading={props.isLoading}
                    errorMessage={props.errorMessage}
                    closedLeadAutomations={props.closedLeadAutomations}
                />
                {groupIntegrationsByActiveStatus(integrationsList)}
            </div>
        );
    }
}
